import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import useScript from "../lib/useScript";
import { CARTAL_CONTACT } from "../lib/contactInfo";
import { Loader2, ShieldCheck, CreditCard, AlertTriangle, ArrowLeft } from "lucide-react";
import { launchPrice } from "../lib/launchDiscount";

// iter109c — buyer-safety flag. Kept `true` by default because the
// hosted-invoice redirect is the most reliable path for MOST purposes
// (subscription / listing_fee / boost).
//
// iter148 exception: for `purpose=escrow_deposit` we **must** use the
// embedded Moyasar Payment Form (MPF) because Moyasar's product surface
// does NOT support Authorization Hold via the hosted Invoice API
// (confirmed in writing by Moyasar support on 2026-02). MPF with
// `credit_card.manual=true` is the ONLY documented way to reach the
// `authorized` state that CARTAL Escrow requires.
const HOSTED_INVOICE_DEFAULT = true;

const MOYASAR_JS_URL = "https://cdn.moyasar.com/mpf/1.16.0/moyasar.js";
const MOYASAR_CSS_URL = "https://cdn.moyasar.com/mpf/1.16.0/moyasar.css";

/**
 * CARTAL — hosted Moyasar checkout page.
 *
 * Query params expected:
 *   ?purpose=listing_fee|subscription|escrow_deposit
 *   &sku=…                      (for listing_fee / subscription)
 *   &reference_id=…             (vehicle_id for boost/escrow)
 *   &amount_sar=…               (escrow only)
 *
 * Additive — no existing checkout page is modified.
 */
export default function MoyasarCheckoutPage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const purpose = params.get("purpose") || "listing_fee";
  const sku = params.get("sku") || undefined;
  const reference_id = params.get("reference_id") || undefined;
  const amount_sar = params.get("amount_sar") ? Number(params.get("amount_sar")) : undefined;

  // iter148 — Escrow deposits MUST use MPF (embedded form) because
  // Authorization Hold is only supported via Create Payment API +
  // credit_card.manual=true, NOT via the hosted invoice. All other
  // purposes keep the hosted-invoice redirect for maximum reliability.
  const SHOW_ONLY_HOSTED_INVOICE = HOSTED_INVOICE_DEFAULT && purpose !== "escrow_deposit";

  // Ref to the persistent Moyasar mount div. The div is rendered
  // UNCONDITIONALLY at the top of the JSX tree so it is always available
  // in the DOM by the time this effect fires — this eliminates the race
  // condition that caused "Element: null is not a valid element" (iter94b).
  // Track whether Moyasar actually injected its DOM into our mount. If
  // the form is silently empty (CSS blocked, iframe blocked by CSP,
  // adblocker, etc.) we surface a fallback message after 4 seconds.
  const [formVisible, setFormVisible] = useState(false);
  const mountRef = useRef(null);
  const [domTick, setDomTick] = useState(0);
  const [creating, setCreating] = useState(true);
  const [payment, setPayment] = useState(null);
  const [err, setErr] = useState(null);
  const [inited, setInited] = useState(false);
  // Separate from `err`: a Moyasar payment-attempt failure. Keeping this
  // in its own state means the form stays mounted so the buyer can retry
  // with a different card / method without leaving the page (iter94 fix).
  const [payFailure, setPayFailure] = useState(null);

  // iter101 — Hosted-invoice fallback state. When the buyer taps
  // "الدفع عبر صفحة ميسر الآمنة" we call POST /api/payments/invoice-create
  // which returns Moyasar's hosted checkout URL, then window.location.href
  // redirects there. Zero iframe = zero ITP / CSP / rendering issues.
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);

  const { loaded: jsLoaded, error: jsErr } = useScript(MOYASAR_JS_URL);

  // Inject CSS once
  useEffect(() => {
    if (document.querySelector(`link[href="${MOYASAR_CSS_URL}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = MOYASAR_CSS_URL;
    document.head.appendChild(l);
  }, []);

  // 1. Create the pending payment on the backend
  useEffect(() => {
    let alive = true;
    // iter102 HIGH fix: distinguish loading (undefined) from anonymous (null).
    // Previously `if (!user)` treated the auth-loading state as anonymous
    // and immediately redirected to /login, so a direct URL / hard-reload
    // / QR / email link to /checkout/moyasar was permanently broken —
    // the buyer ended up in a redirect loop that landed on /dashboard.
    if (user === undefined) return;   // still loading /auth/me — wait
    if (user === null) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    (async () => {
      setCreating(true);
      setErr(null);
      try {
        // If the URL has `?only=card`, forward it so the backend disables
        // Apple Pay + STC Pay for this single transaction — useful to
        // isolate credit-card rendering issues (iter95d).
        const onlyCard = params.get("only") === "card";
        const url = onlyCard ? "/payments/create?only=card" : "/payments/create";
        const { data } = await apiClient.post(url, {
          purpose,
          sku,
          reference_id,
          amount_sar,
          provider: "moyasar",
          apple_pay_enabled: !onlyCard,
          stc_pay_enabled: !onlyCard,
        });
        if (!alive) return;
        setPayment(data);
      } catch (e) {
        if (!alive) return;
        setErr(formatApiError(e));
      } finally {
        if (alive) setCreating(false);
      }
    })();
    return () => { alive = false; };
  }, [purpose, sku, reference_id, amount_sar, user, navigate]);

  // 2. Initialise the Moyasar Payment Form after both the SDK and the
  //    backend-created payment are ready.
  useEffect(() => {
    if (SHOW_ONLY_HOSTED_INVOICE) return;  // iter109c — SDK dormant when frozen
    if (!jsLoaded || !payment || inited) return;
    if (!window.Moyasar) return;
    const cfg = payment.form_config;
    if (!cfg?.publishable_api_key) return;  // gateway not configured yet

    // Belt-and-suspenders: even though the mount div is now rendered
    // unconditionally, we ALSO verify it exists in the DOM before calling
    // Moyasar.init(). If for any reason it's still not there (extremely
    // unusual React lifecycle edge), retry on the next animation frame.
    // This eliminates "Element: null is not a valid element" (iter94b).
    const mountEl = document.getElementById("cartal-moyasar-form");
    if (!mountEl) {
      const id = setTimeout(() => setDomTick((t) => t + 1), 16);
      return () => clearTimeout(id);
    }

    try {
      // Only include options if they are actually set. Passing `undefined`
      // is safe (SDK ignores it) but passing `null` for `apple_pay` can
      // trip the SDK's Apple Pay init path on some browsers — safer to
      // omit the key entirely when we don't want Apple Pay (iter93 fix).
      const initOpts = {
        element: "#cartal-moyasar-form",
        amount: cfg.amount,
        currency: cfg.currency,
        description: cfg.description,
        publishable_api_key: cfg.publishable_api_key,
        callback_url: cfg.callback_url,
        methods: cfg.methods,
        supported_networks: cfg.supported_networks,
        metadata: cfg.metadata,
        // Manual capture — for CARTAL Guarantee (escrow_deposit) the
        // Moyasar form creates the payment with `source.manual: true`,
        // producing an `authorized` (held) status. Backend later captures
        // on delivery confirmation or voids on cancellation.
        manual: !!cfg.manual,
        language: lang === "ar" ? "ar" : "en",
        on_completed: async (result) => {
          // Reliability net: persist immediately even if the browser is
          // closed before the redirect completes.
          try {
            await apiClient.post("/payments/moyasar/completed", {
              payment_id: payment.id,
              provider_payment_id: result.id,
            });
          } catch { /* webhook will retry */ }
          return { moyasar_id: result.id };
        },
        // Bubble any Moyasar-side create-payment failure back to our UI so
        // the buyer doesn't sit in front of a frozen button forever.
        // IMPORTANT: use `payFailure` (not `err`) so the form stays mounted
        // and the buyer can retry with a different card/method without a
        // full-page reload.
        on_failure: (err) => {
          console.error("[Moyasar] payment failed:", err);
          let rawText = "";
          try { rawText = JSON.stringify(err, Object.getOwnPropertyNames(err || {}), 2); }
          catch { rawText = String(err); }
          console.error("[Moyasar] failure JSON:", rawText);

          // Show a preliminary failure banner immediately.
          setPayFailure({
            when: new Date().toISOString(),
            message:
              err?.message ||
              err?.error?.message ||
              err?.errors?.[0]?.message ||
              (typeof err === "string" ? err : null),
            raw: err,
            rawText,
            probe: null,
          });

          // Then probe our backend for the REAL Moyasar error (the SDK's
          // "unknown error" hides the actual source.message from Moyasar).
          // When the probe returns, we replace the banner content with
          // the enriched message (iter95c).
          (async () => {
            try {
              const { data } = await apiClient.post("/payments/moyasar/client-failure", {
                payment_id: payment.id,
                purpose: payment.purpose,
                amount_sar: payment.amount_sar,
                methods: cfg.methods,
                raw: rawText.slice(0, 4000),
                user_agent: navigator.userAgent,
                origin: window.location.origin,
              });
              const probe = data?.probe;
              const parsed = probe?.parsed;
              if (parsed) {
                setPayFailure((prev) => ({
                  ...(prev || {}),
                  message:
                    parsed.source_message || parsed.message || (prev && prev.message) ||
                    `Moyasar status: ${parsed.status}`,
                  probe,
                }));
              } else if (probe?.body) {
                setPayFailure((prev) => ({ ...(prev || {}), probe }));
              }
            } catch (probeErr) {
              console.error("[Moyasar] probe failed:", probeErr);
            }
          })();
        },
      };
      // Apple Pay config — Moyasar MPF v1.14 REJECTS init entirely with
      // "Validate Merchant URL is required for Apple Pay" unless this URL
      // is set. We ALWAYS override with the current browser origin
      // regardless of what the backend sent, so it works on `cartal.sa`,
      // preview, local dev, and staging without env-var juggling.
      if (cfg.apple_pay) {
        initOpts.apple_pay = {
          ...cfg.apple_pay,
          validate_merchant_url:
            `${window.location.origin}/api/payments/apple-pay/validate-merchant`,
        };
      }

      window.Moyasar.init(initOpts);
      setInited(true);

      // Auto-scroll the mount into view after Moyasar injects its iframe
      // so the buyer sees the card fields immediately (previously they
      // could be below Apple Pay button, out of the viewport — iter97).
      setTimeout(() => {
        const el = document.getElementById("cartal-moyasar-form");
        if (el && el.children.length > 0) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 1200);

      // Empty-form watchdog: after 4s check if Moyasar actually injected
      // any child nodes into our mount div. If not, the SDK silently
      // failed to render (common on iOS 18 Safari with Private Relay,
      // strict cookie blocking, or CSP frame-src restrictions). Log the
      // condition to backend and surface a helpful message to the buyer.
      setTimeout(() => {
        const el = document.getElementById("cartal-moyasar-form");
        const hasContent = !!el && el.children.length > 0;
        setFormVisible(hasContent);
        if (!hasContent) {
          console.error("[Moyasar] mount empty 4s after init — iframe blocked or CSS missing");
          try {
            apiClient.post("/payments/moyasar/client-failure", {
              payment_id: payment.id,
              purpose: payment.purpose,
              amount_sar: payment.amount_sar,
              methods: cfg.methods,
              raw: "empty_mount_after_init",
              user_agent: navigator.userAgent,
              origin: window.location.origin,
            }).catch(() => {});
          } catch { /* logging best-effort */ }
        }
      }, 4000);
    } catch (e) {
      console.error("[Moyasar] init threw:", e);
      setErr(e?.message || "Moyasar init failed");
    }
  }, [jsLoaded, payment, inited, lang, domTick]);

  const notConfigured = payment && payment.gateway_configured === false;

  const summary = useMemo(() => {
    if (!payment) return null;
    return {
      amount: payment.amount_sar,
      purpose: payment.purpose,
      sku: payment.sku,
    };
  }, [payment]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="moyasar-checkout-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-[#6b6b6b] hover:text-[#0a0a0a] mb-6"
        data-testid="checkout-back"
      >
        <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
        {lang === "ar" ? "رجوع" : "Back"}
      </button>

      <div className="rounded-3xl overflow-hidden border-2 relative"
           style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1a1611 100%)", borderColor: "#c9a55c" }}>
        <div className="p-6 sm:p-8">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "#c9a55c" }}>
            {lang === "ar" ? "الدفع الآمن عبر Moyasar" : "Secure checkout · Moyasar"}
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            {(() => {
              // iter106 — surface the launch discount on the hero amount.
              // For escrow deposits the discount is already reflected in
              // the amount server-side (see DealCompletionPage); for
              // subscriptions & boosts we treat the shown amount as the
              // discounted price and render the original + savings pill.
              const info = summary && payment?.purpose !== "escrow_deposit"
                ? launchPrice(Number(summary.amount))
                : null;
              return (
                <>
                  <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white">
                    {summary
                      ? `${Number(summary.amount).toLocaleString()} ${lang === "ar" ? "ر.س" : "SAR"}`
                      : (lang === "ar" ? "جاري تجهيز الدفعة…" : "Preparing…")}
                  </h1>
                  {info && info.active && (
                    <>
                      <span
                        className="font-display text-lg sm:text-xl line-through decoration-2"
                        style={{ color: "rgba(232,225,207,0.5)", textDecorationColor: "#ef4444" }}
                        data-testid="checkout-original-amount"
                      >
                        {info.original.toLocaleString()}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5"
                        style={{ background: "rgba(239,68,68,0.20)", color: "#fecaca", border: "1px solid rgba(239,68,68,0.55)" }}
                        data-testid="checkout-savings-pill"
                      >
                        {lang === "ar" ? `خصم 50٪ · وفّرت ${info.savings} ر.س` : `50% OFF · Save ${info.savings} SAR`}
                      </span>
                    </>
                  )}
                  {summary && (
                    <span className="text-xs w-full sm:w-auto" style={{ color: "#e8e1cf" }}>
                      {payment?.description}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-[11px]" style={{ color: "#e8e1cf" }}>
            <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" style={{ color: "#c9a55c" }} />
              {lang === "ar" ? "بيانات البطاقة لا تلمس خوادم كارتال" : "Card data never touches CARTAL servers"}
            </span>
            <span className="inline-flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" style={{ color: "#c9a55c" }} />
              Mada · Visa · Mastercard · Apple Pay
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-3xl bg-white border border-[#0a0a0a]/10 p-6 sm:p-8" data-testid="checkout-body">
        {creating && (
          <div className="flex items-center justify-center py-16 text-[#6b6b6b]" data-testid="checkout-loading">
            <Loader2 className="w-5 h-5 animate-spin me-2" />
            {lang === "ar" ? "جاري التحقق من الطلب…" : "Verifying order…"}
          </div>
        )}

        {err && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="checkout-error">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <div className="text-sm text-red-800">{err}</div>
          </div>
        )}

        {!creating && !err && payment && notConfigured && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="checkout-not-configured">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
              <div className="text-sm text-amber-900 leading-relaxed">
                {lang === "ar" ? (
                  <>
                    بوابة الدفع لم تُفعَّل بعد على هذا الموقع. تم إنشاء طلب الدفع الخاص بك برقم <span className="font-mono font-bold">{payment.id}</span> بمبلغ <b>{payment.amount_sar} ر.س</b> وسيصبح جاهزاً فور إضافة مفاتيح <b>Moyasar</b> في إعدادات البيئة.<br />
                    للاستفسار: <a className="underline" href={CARTAL_CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer">واتساب الدعم</a>.
                  </>
                ) : (
                  <>
                    The payment gateway has not been enabled on this site yet. Your payment order <span className="font-mono font-bold">{payment.id}</span> for <b>{payment.amount_sar} SAR</b> is created and will be processable as soon as <b>Moyasar</b> keys are added to the environment.<br />
                    Contact: <a className="underline" href={CARTAL_CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer">WhatsApp support</a>.
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {!creating && !err && payment && !notConfigured && (
          <>
            {/* Payment methods — visible so the buyer sees the supported
                networks even before the Moyasar iframe finishes loading.
                The badges are driven by `form_config.methods` so we only
                advertise methods that will actually render (e.g. escrow
                holds are card-only, no Apple Pay / STC Pay).
                Frozen behind SHOW_ONLY_HOSTED_INVOICE (iter109c). */}
            {!SHOW_ONLY_HOSTED_INVOICE && (() => {
              const activeMethods = payment.form_config?.methods || [];
              const showCard = activeMethods.includes("creditcard");
              const showApple = activeMethods.includes("applepay");
              return (
                <div className="mb-5 rounded-2xl p-4 border" style={{ background: "#faf7f0", borderColor: "#e8ddc5" }} data-testid="payment-methods-strip">
                  <div className="text-[11px] font-bold uppercase tracking-wider mb-2.5" style={{ color: "#8a6f2e" }}>
                    {lang === "ar" ? "البطاقات المدعومة — اضغط للانتقال إلى النموذج" : "Supported cards — tap to jump to the form"}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {showCard && (
                      <>
                        {[
                          { key: "mada",  label: (<span dir="ltr">مدى · mada</span>), bg: "#84cc16", fg: "#052e16", border: "#4d7c0f" },
                          { key: "visa",  label: <span dir="ltr">VISA</span>, bg: "#1a1f71", fg: "#ffffff", border: "#0d1350" },
                          { key: "mc",    label: (
                            <span dir="ltr" className="inline-flex items-center">
                              <span className="w-3 h-3 rounded-full" style={{ background: "#eb001b", display: "inline-block", marginInlineEnd: "-6px" }} />
                              <span className="w-3 h-3 rounded-full" style={{ background: "#f79e1b", display: "inline-block", opacity: 0.9 }} />
                              <span className="ms-1">Mastercard</span>
                            </span>), bg: "#ffffff", fg: "#0a0a0a", border: "#d1d5db" },
                        ].map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => {
                              const el = document.getElementById("cartal-moyasar-form");
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                            }}
                            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-black tracking-wide transition-transform active:scale-95"
                            style={{ background: p.bg, color: p.fg, border: `1px solid ${p.border}` }}
                            data-testid={`brand-pill-${p.key}`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </>
                    )}
                    {showApple && (
                      <span className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-black tracking-wide"
                            style={{ background: "#0a0a0a", color: "#ffffff", border: "1px solid #0a0a0a", cursor: "default" }} dir="ltr">
                        Apple Pay
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-[11px] font-bold flex items-center gap-1" style={{ color: "#8a6f2e" }}>
                    <span>↓</span>
                    <span>
                      {lang === "ar"
                        ? "أدخل بيانات بطاقتك في النموذج الآمن أدناه — الشبكة تُكتشف تلقائياً."
                        : "Enter your card details in the secure form below — network detected automatically."}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Payment attempt failed — verbatim Moyasar error, form
                STAYS visible so buyer can retry with another card/method.
                Includes a copy-to-clipboard button so the buyer can send
                the exact error to support in one tap (iter94). */}
            {payFailure && (
              <div className="mb-4 rounded-2xl border-2 border-red-300 bg-red-50 p-4" data-testid="moyasar-pay-failure">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-red-900 mb-1">
                      {lang === "ar" ? "تعذّر إتمام الدفع" : "Payment could not be completed"}
                    </div>
                    <div className="text-sm text-red-800 break-words">
                      {payFailure.message ||
                        (lang === "ar"
                          ? "استجابة غير متوقّعة من بوابة الدفع. جرّب بطاقة أخرى، أو راسلنا للمساعدة."
                          : "Unexpected response from the gateway. Try a different card or contact support.")}
                    </div>

                    {/* ── Raw error — VISIBLE by default (no click needed) ── */}
                    <div className="mt-3 rounded-lg border border-red-200 bg-white/60 p-2">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-red-800 mb-1">
                        {lang === "ar" ? "استجابة Moyasar SDK (المتصفح)" : "Moyasar SDK response (browser)"}
                      </div>
                      <pre className="max-h-40 overflow-auto font-mono text-[10px] leading-tight whitespace-pre-wrap break-words text-red-900" dir="ltr" data-testid="moyasar-failure-raw">
{payFailure.rawText || (() => { try { return JSON.stringify(payFailure.raw, null, 2); } catch { return String(payFailure.raw); } })()}
                      </pre>
                    </div>

                    {/* ── Server-side probe of Moyasar API — the REAL error ── */}
                    {payFailure.probe && (
                      <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2" data-testid="moyasar-failure-probe">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-900 mb-1">
                          {lang === "ar"
                            ? `الاستجابة الحقيقية من Moyasar API (HTTP ${payFailure.probe.status_code ?? "?"})`
                            : `Actual Moyasar API response (HTTP ${payFailure.probe.status_code ?? "?"})`}
                        </div>
                        {payFailure.probe.parsed && (
                          <div className="mb-1 text-[11px] text-amber-900 space-y-0.5">
                            <div><b>status:</b> <span dir="ltr">{payFailure.probe.parsed.status}</span></div>
                            {payFailure.probe.parsed.source_type && <div><b>method:</b> <span dir="ltr">{payFailure.probe.parsed.source_type}</span></div>}
                            {payFailure.probe.parsed.source_message && <div><b>gateway message:</b> <span dir="ltr">{payFailure.probe.parsed.source_message}</span></div>}
                            {payFailure.probe.parsed.message && <div><b>moyasar message:</b> <span dir="ltr">{payFailure.probe.parsed.message}</span></div>}
                          </div>
                        )}
                        <pre className="max-h-40 overflow-auto font-mono text-[10px] leading-tight whitespace-pre-wrap break-words text-amber-900" dir="ltr">
{typeof payFailure.probe.body === "string" ? payFailure.probe.body : JSON.stringify(payFailure.probe.body, null, 2)}
                        </pre>
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          const text = (() => {
                            try {
                              return JSON.stringify({
                                payment_id: payment.id,
                                purpose: payment.purpose,
                                amount_sar: payment.amount_sar,
                                when: payFailure.when,
                                message: payFailure.message,
                                raw_text: payFailure.rawText,
                                sdk: (typeof window !== "undefined" && window.Moyasar) ? "loaded" : "missing",
                                user_agent: navigator.userAgent,
                              }, null, 2);
                            } catch { return String(payFailure.raw); }
                          })();
                          try { await navigator.clipboard.writeText(text); alert(lang === "ar" ? "تم نسخ التفاصيل" : "Details copied"); }
                          catch { window.prompt(lang === "ar" ? "انسخ التفاصيل يدوياً:" : "Copy details manually:", text); }
                        }}
                        className="rounded-full px-3 py-1.5 text-[11px] font-bold bg-red-900 text-red-50 hover:bg-red-800"
                        data-testid="moyasar-copy-failure"
                      >
                        {lang === "ar" ? "نسخ الكل للدعم" : "Copy all for support"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPayFailure(null);
                          const el = document.getElementById("cartal-moyasar-form");
                          if (el) el.innerHTML = "";
                          setInited(false);
                        }}
                        className="rounded-full px-4 py-1.5 text-xs font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a]"
                        data-testid="moyasar-retry"
                      >
                        {lang === "ar" ? "أعِد المحاولة" : "Retry"}
                      </button>
                      <a
                        href={CARTAL_CONTACT.whatsappUrl}
                        target="_blank" rel="noopener noreferrer"
                        className="rounded-full px-4 py-1.5 text-xs font-bold bg-white border border-red-300 text-red-800 hover:bg-red-100"
                      >
                        {lang === "ar" ? "تواصل مع الدعم" : "Contact support"}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!jsLoaded && !jsErr && (
              <div className="flex items-center justify-center py-8 text-[#6b6b6b] text-sm">
                <Loader2 className="w-4 h-4 animate-spin me-2" />
                {lang === "ar" ? "تحميل واجهة الدفع الآمنة…" : "Loading secure payment UI…"}
              </div>
            )}
            {jsErr && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                {lang === "ar" ? "تعذّر تحميل بوابة الدفع." : "Could not load payment gateway."}
              </div>
            )}
          </>
        )}

        {/* Prominent "Enter card here ↓" callout — sits immediately above
            the Moyasar mount so the buyer never wonders where to enter
            their card details. Added iter97 after users mistook the
            static brand pills for clickable payment tabs. */}
        {!creating && !err && payment && !notConfigured && jsLoaded && !jsErr && (
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("cartal-moyasar-form");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="mt-4 mb-2 flex items-center gap-3 rounded-2xl border-2 p-3 w-full text-start transition-transform active:scale-[0.98]"
            style={{ borderColor: "#c9a55c", background: "linear-gradient(135deg,#fbf6e8 0%,#f5efe0 100%)" }}
            data-testid="enter-card-callout">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: "#0a0a0a", color: "#c9a55c" }}>
              <span className="text-lg font-bold">↓</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[#0a0a0a]">
                {lang === "ar" ? "أدخل بيانات بطاقتك هنا" : "Enter your card details here"}
              </div>
              <div className="text-[11px] text-[#6b6b6b] leading-snug mt-0.5">
                {lang === "ar"
                  ? "رقم البطاقة · الاسم · تاريخ الانتهاء · CVC · ثم اضغط «ادفع»"
                  : "Card number · Name · Expiry · CVC · then tap Pay"}
              </div>
            </div>
          </button>
        )}

        {/* iter101 — HOSTED INVOICE FLOW (recommended path).
            Redirects the buyer to Moyasar's own checkout page instead of
            embedding an iframe. Bypasses ALL browser-level restrictions
            (iOS Safari 18 ITP, Chrome corporate MDM, ad-blockers, CSP,
            third-party cookie blocks). This is now the PRIMARY payment
            entry point — the embedded MPF form below is kept as an
            alternate option for buyers who prefer to stay on our site. */}
        {!creating && !err && payment && !notConfigured && (
          <div className="mt-2 mb-4 rounded-2xl border-2 p-4"
               style={{ borderColor: "#c9a55c", background: "linear-gradient(135deg,#0a0a0a 0%,#1a1611 100%)" }}
               data-testid="hosted-invoice-cta">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "#c9a55c" }}>
                {lang === "ar" ? "الطريقة الموصى بها" : "Recommended"}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: "#c9a55c", color: "#0a0a0a" }}>
                {lang === "ar" ? "الأسرع" : "Fastest"}
              </span>
            </div>
            <div className="text-white text-sm leading-relaxed mb-3">
              {lang === "ar"
                ? "ادفع مباشرة عبر صفحة ميسر الآمنة — يعمل على جميع الأجهزة والمتصفحات (iPhone / Android / Chrome / Safari) ويدعم مدى، فيزا، ماستركارد، وApple Pay."
                : "Pay directly through Moyasar's secure hosted page — works on all devices and browsers (iPhone / Android / Chrome / Safari) and supports Mada, Visa, Mastercard, Apple Pay."}
            </div>
            <button
              type="button"
              disabled={invoiceLoading}
              onClick={async () => {
                setInvoiceError(null);
                setInvoiceLoading(true);
                try {
                  const { data } = await apiClient.post("/payments/invoice-create", {
                    purpose,
                    sku,
                    reference_id,
                    amount_sar,
                    provider: "moyasar",
                    apple_pay_enabled: true,
                    stc_pay_enabled: false,
                  });
                  if (data && data.invoice_url) {
                    // Direct top-level navigation — no iframe, no popup, no
                    // Storage-Access-API dance. Works on every browser.
                    window.location.href = data.invoice_url;
                  } else {
                    throw new Error("no invoice url");
                  }
                } catch (e) {
                  setInvoiceLoading(false);
                  setInvoiceError(
                    formatApiError(e) ||
                    (lang === "ar"
                      ? "تعذّر إنشاء رابط الدفع. أعد المحاولة."
                      : "Could not create the payment link. Please retry.")
                  );
                }
              }}
              className="w-full rounded-full px-6 py-3.5 text-base font-black tracking-wide transition-transform active:scale-95 disabled:opacity-60"
              style={{ background: "#c9a55c", color: "#0a0a0a" }}
              data-testid="hosted-invoice-button"
            >
              {invoiceLoading
                ? (lang === "ar" ? "جارٍ التحويل…" : "Redirecting…")
                : (lang === "ar" ? "ادفع الآن عبر صفحة ميسر الآمنة" : "Pay now on Moyasar's secure page")}
            </button>
            {invoiceError && (
              <div className="mt-3 text-xs text-red-300" data-testid="hosted-invoice-error">
                {invoiceError}
              </div>
            )}
            {!SHOW_ONLY_HOSTED_INVOICE && (
              <div className="mt-3 text-[11px] text-white/60 text-center">
                {lang === "ar"
                  ? "أو استخدم النموذج المدمج أدناه (قد لا يعمل على بعض الأجهزة)"
                  : "Or use the embedded form below (may not work on some devices)"}
              </div>
            )}
          </div>
        )}

        {/* Moyasar mount — ALWAYS in the DOM so `document.getElementById`
            never returns null when Moyasar.init() runs. Kept as a
            secondary option below the hosted-invoice CTA (iter101).
            Visually hidden behind SHOW_ONLY_HOSTED_INVOICE (iter109c) —
            still present in DOM to satisfy the SDK's mount requirement. */}
        <div
          ref={mountRef}
          id="cartal-moyasar-form"
          className="mysr-form min-h-[220px]"
          data-testid="moyasar-form-mount"
          style={{
            display: SHOW_ONLY_HOSTED_INVOICE
              ? "none"
              : (!creating && !err && payment && !notConfigured && jsLoaded && !jsErr
                  ? "block"
                  : "none"),
          }}
        />

        {/* Empty-form fallback: if Moyasar didn't inject its iframe within
            4 seconds (see empty-form watchdog in the init effect), show a
            helpful message with troubleshooting steps. Common triggers:
            iOS Safari Private Relay, ad-blocker, corporate MDM policy. */}
        {inited && !formVisible && !payFailure && (
          <div className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="moyasar-empty-fallback">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-amber-900 mb-1">
                  {lang === "ar" ? "لم يظهر نموذج الدفع" : "The payment form didn't appear"}
                </div>
                <div className="text-sm text-amber-800 leading-relaxed">
                  {lang === "ar"
                    ? "يبدو أن متصفحك يحجب iframe الدفع الآمن من ميسر. جرّب الحلول التالية:"
                    : "It looks like your browser is blocking the secure Moyasar iframe. Try these fixes:"}
                </div>
                <ul className="mt-2 text-xs text-amber-900 space-y-1 list-disc list-inside">
                  <li>{lang === "ar" ? "أوقف Private Relay في الإعدادات (iOS)" : "Turn off Private Relay in iOS Settings"}</li>
                  <li>{lang === "ar" ? "أوقف مانعات الإعلانات وامتدادات الخصوصية" : "Disable ad-blockers or privacy extensions"}</li>
                  <li>{lang === "ar" ? "جرّب متصفح Chrome / Safari (غير خصوصي)" : "Try Chrome / regular Safari (not private mode)"}</li>
                  <li>{lang === "ar" ? "أعِد المحاولة على واي فاي أو شبكة أخرى" : "Retry over WiFi or a different network"}</li>
                </ul>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="rounded-full px-4 py-1.5 text-xs font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a]"
                    data-testid="moyasar-fallback-reload"
                  >
                    {lang === "ar" ? "أعِد تحميل الصفحة" : "Reload the page"}
                  </button>
                  <a href={CARTAL_CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer"
                     className="rounded-full px-4 py-1.5 text-xs font-bold bg-white border border-amber-300 text-amber-800 hover:bg-amber-100">
                    {lang === "ar" ? "تواصل مع الدعم" : "Contact support"}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="mt-4 text-[11px] text-[#8a8a8a] leading-relaxed text-center">
        {lang === "ar"
          ? "بإتمام الدفع فأنت توافق على شروط كارتال. جميع المعاملات مشفّرة ومحمية عبر Moyasar."
          : "By completing payment you agree to CARTAL's terms. All transactions are encrypted and secured by Moyasar."}
      </p>
    </div>
  );
}
